import * as React from "react";
import {useEffect, useState} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import {
    Box,
    DialogActions,
    DialogContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import {LoadingButton} from "@mui/lab";
import PropTypes from "prop-types";
import {useBulkUpdateReadyCommentMutation} from "../../core/api/products-api";
import {useGetActiveOrderCommentTemplatesQuery} from "../../core/api/order-comment-api";

const MenuProps = {
    PaperProps: {
        sx: {
            maxWidth: "fit-content"
        },
    },
};

export default function BulkReadyCommentDialog(props) {
    const {onClose, open, productIds} = props;
    const {data: commentTemplates = []} = useGetActiveOrderCommentTemplatesQuery();
    const [bulkUpdate, bulkUpdateResponse] = useBulkUpdateReadyCommentMutation();
    const [state, setState] = useState({
        readyCommentTemplateId: "",
        readyCommentText: "",
        isSubmitted: false,
        errors: {}
    });

    useEffect(() => {
        if (state.isSubmitted &&
            (bulkUpdateResponse.isSuccess || bulkUpdateResponse.isError)) {
            setState({
                ...state,
                isSubmitted: false
            })
            onClose(true, bulkUpdateResponse);
        }
    }, [bulkUpdateResponse])

    const handleTemplateChange = (event) => {
        setState({
            ...state,
            readyCommentTemplateId: event.target.value,
            errors: {}
        })
    }

    const handleChangeText = (event) => {
        setState({
            ...state,
            readyCommentText: event.target.value,
            errors: {}
        })
    }

    const getSelectedTemplate = () => {
        return commentTemplates.find(template => template.id === state.readyCommentTemplateId)
    }

    const handleClose = () => {
        onClose(false);
    }

    const handleSaveButton = () => {
        if (state.readyCommentTemplateId === "" && state.readyCommentText.trim() === "") {
            setState({
                ...state,
                errors: {readyCommentText: true}
            })
            return;
        }
        setState({
            ...state,
            isSubmitted: true
        })
        bulkUpdate({
            productIds: productIds,
            hasReadyComment: true,
            readyCommentTemplateId: state.readyCommentTemplateId,
            readyCommentText: state.readyCommentTemplateId === "" ? state.readyCommentText : ""
        });
    }

    return (
        <Dialog onClose={handleClose} open={open} fullWidth maxWidth="sm">
            <DialogTitle>
                {'Auto-comment in the task on "Done" status'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{mb: 2}} color="text.secondary">
                    Will be set for {productIds.length} selected product{productIds.length === 1 ? "" : "s"}.
                </Typography>
                <FormControl fullWidth sx={{mt: 1}}>
                    <InputLabel color="warning" id="bulk-ready-comment-template-label">
                        Auto-comment template
                    </InputLabel>
                    <Select
                        color="warning"
                        labelId="bulk-ready-comment-template-label"
                        id="bulk-ready-comment-template-select"
                        value={state.readyCommentTemplateId}
                        label="Auto-comment template"
                        onChange={handleTemplateChange}
                        MenuProps={MenuProps}
                    >
                        <MenuItem key="custom" value="">Custom text</MenuItem>
                        {commentTemplates.map(template =>
                            <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                        )}
                    </Select>
                </FormControl>
                {state.readyCommentTemplateId !== "" && getSelectedTemplate() &&
                    <Box sx={{mt: 1, p: 1.5, bgcolor: "grey.100", borderRadius: 1,
                        whiteSpace: "pre-wrap", fontSize: 14, color: "text.secondary"}}>
                        {getSelectedTemplate().text}
                    </Box>
                }
                {state.readyCommentTemplateId === "" &&
                    <TextField
                        required
                        sx={{mt: 2}}
                        value={state.readyCommentText}
                        id="bulk-ready-comment-text"
                        label="Auto-comment text"
                        variant="outlined"
                        color="warning"
                        onChange={handleChangeText}
                        fullWidth
                        multiline
                        minRows={3}
                        name="readyCommentText"
                        error={state.errors.readyCommentText}
                        helperText={state.errors.readyCommentText
                            ? "Select a template or provide custom text"
                            : ""}
                    />
                }
            </DialogContent>
            <DialogActions>
                <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <LoadingButton
                    size="small"
                    onClick={handleSaveButton}
                    loading={state.isSubmitted && bulkUpdateResponse.isLoading}
                    variant="contained"
                    color="warning"
                >
                    Apply
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

BulkReadyCommentDialog.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    productIds: PropTypes.arrayOf(PropTypes.string).isRequired
};
